import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { List, ListItem } from 'chaoskit/src/components';

import ChaosMonkeyLayout from '../layouts/ChaosMonkeyLayout';
import ChaosMonkeyHero from '../partials/chaos-monkey/Hero';
import { Data } from '../components';

const ChaosMonkey = ({
  data: {
    guideChapters: { edges },
    pageData: { childDataYaml: pageData },
  },
}) => (
  <ChaosMonkeyLayout>
    <Helmet
      title={pageData.seoTitle}
      meta={[
        { name: 'description', content: pageData.description },
        {
          property: 'og:image',
          content: pageData.socialMediaImage,
        },
      ]}
    />
    <ChaosMonkeyHero background="https://res.cloudinary.com/gremlin/image/upload/c_limit,q_80,w_2000/ChaosMonkey-Home-Hero.svg">
      <div className="hero-content">
        <div className="container">
          <div className="hero-titleWrapper">
            <h1 className="hero-title hero-title--large">
              <Data>{pageData.title}</Data>
            </h1>
            <h5 className="hero-titleMeta">
              <Data>{pageData.titleSub}</Data>
            </h5>
          </div>
        </div>
      </div>
    </ChaosMonkeyHero>
    <main className="chaosMonkey__base">
      <section className="section chaosMonkey__content">
        <div className="container container--small">
          <h3 className="u-mb--remove u-textCenter u-textNormal u-textChaosMonkey">
            In 2010 Netflix announced the existence and success of their custom{' '}
            <a
              className="u-linkChaosMonkey u-textUnderline"
              href="https://medium.com/netflix-techblog/5-lessons-weve-learned-using-aws-1f2a28588e4c"
              target="_blank"
              rel="noopener noreferrer"
            >
              resiliency tool
            </a>{' '}
            called <span className="u-textItalic">Chaos Monkey</span>.
          </h3>

          <h3 className="chaosMonkey__sectionTitle--large">
            <Data>{pageData.intro.heading}</Data>
          </h3>

          <div className="u-mv--xlarge">
            <Data markdown>{pageData.intro.firstBlurb}</Data>
          </div>

          <h5 className="u-textMedium u-textChaosMonkey u-textCenter">
            Chaos Engineering Is
          </h5>
          <h3 className="u-textCenter u-textNormal u-textMuted u-mb--xlarge">
            &quot;the discipline of experimenting on a distributed system in
            order to build confidence in the system&apos;s capability to
            withstand turbulent conditions in production.&quot;
          </h3>

          <Data markdown>{pageData.intro.secondBlurb}</Data>

          <h3 className="chaosMonkey__sectionTitle--large">
            <Data>{pageData.what.heading}</Data>
          </h3>
          <Data markdown>{pageData.what.blurb}</Data>

          <h3 className="chaosMonkey__sectionTitle--large">
            <Data>{pageData.who.heading}</Data>
          </h3>
          <Data markdown>{pageData.who.blurb}</Data>

          <h3 className="chaosMonkey__sectionTitle--large">
            <Data>{pageData.why.heading}</Data>
          </h3>
          <Data markdown>{pageData.why.blurb}</Data>
        </div>
        <div className="container container--small">
          <h3
            className="chaosMonkey__sectionTitle--large"
            id="chaos-monkey-pros-cons"
          >
            <Data>{pageData.prosCons.heading}</Data>
          </h3>
          <Data markdown>{pageData.prosCons.blurb}</Data>
        </div>
        <div className="container u-mt--xlarge">
          <div className="row row--largeGutter">
            <div className="column-6@medium">
              <div className="chaosMonkey__proCon-box">
                <h3 className="u-textChaosMonkey u-mb--large">
                  {pageData.prosCons.pros.heading}
                </h3>
                <List className="chaosMonkey__proCon-list">
                  {pageData.prosCons.pros.items.map(pro => (
                    <ListItem key={pro.title}>
                      <h5 className="u-mb--remove">
                        <Data>{pro.title}</Data>
                      </h5>
                      <div className="u-mt--small">
                        <Data markdown>{pro.blurb}</Data>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </div>
            </div>
            <div className="column-6@medium">
              <div className="chaosMonkey__proCon-box">
                <h3 className="u-textChaosMonkey u-mb--large">
                  {pageData.prosCons.cons.heading}
                </h3>
                <List className="chaosMonkey__proCon-list">
                  {pageData.prosCons.cons.items.map(con => (
                    <ListItem key={con.title}>
                      <h5 className="u-mb--remove">
                        <Data>{con.title}</Data>
                      </h5>
                      <div className="u-mt--small">
                        <Data markdown>{con.blurb}</Data>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section u-bgChaosMonkeyLight chaosMonkey__guide-section">
        <div className="container">
          <div className="section-titleWrapper">
            <h2 className="section-title u-textFluid--h1-h2 u-textChaosMonkey">
              Guide Chapters
            </h2>
          </div>
          {edges.map(({ node }) => (
            <a
              href={`/chaos-monkey/${node.fields.slug}/`}
              key={node.fileAbsolutePath}
              className="chaosMonkey__articleTeaser"
            >
              <div
                className="chaosMonkey__articleTeaser-media"
                style={{
                  backgroundImage: `url(${node.frontmatter.thumbnailImage})`,
                }}
              />
              <div className="chaosMonkey__articleTeaser-content">
                <h5 className="u-textMuted u-mb--small">
                  <Data>{node.frontmatter.title}</Data>
                </h5>
                <h4 className="u-textDefault u-textFluid--xlarge u-mv--remove">
                  <Data>{node.frontmatter.titleSub}</Data>
                </h4>

                <div className="chaosMonkey__articleTeaser-excerpt u-textSmall u-textDefault">
                  <Data stripMarkdown>{node.excerpt}</Data>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </main>
  </ChaosMonkeyLayout>
);

ChaosMonkey.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ChaosMonkey;

export const pageQuery = graphql`
  query ChaosMonkeyIndexPageData {
    pageData: file(name: { eq: "chaos-monkey__index" }) {
      childDataYaml {
        title
        seoTitle
        titleSub
        socialMediaImage
        description
        intro {
          heading
          firstBlurb
          secondBlurb
        }
        what {
          heading
          blurb
        }
        who {
          heading
          blurb
        }
        why {
          heading
          blurb
        }
        prosCons {
          heading
          blurb
          pros {
            heading
            items {
              title
              blurb
            }
          }
          cons {
            heading
            items {
              title
              blurb
            }
          }
        }
      }
    }

    guideChapters: allMarkdownRemark(
      filter: {
        fileAbsolutePath: {
          regex: "/src/chaos-monkey/(?!chaos-monkey-alternatives/)/"
        }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
      sort: { fields: [frontmatter___priority], order: ASC }
    ) {
      edges {
        node {
          ...ChaosMonkeySingleFragment
        }
      }
    }
  }
`;
